import styled from '@emotion/styled';

import { useTranslation } from 'react-i18next';
import { tokens, Text } from '@hotjar/hotsauce';

import { navigateTo } from '@hotjar/webapp/utils/routing';
import type { RecentSurvey } from '@hotjar/webapp/domains/surveys/types';
import { useCurrentSite } from '@hotjar/site';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { SURVEYS } from '@hotjar/webapp/constants/routes';
import { WidgetGhostButton } from '../WidgetGhostButton';

const Row = styled.tr`
  vertical-align: middle;
  background-color: ${tokens.color.ui_background};

  td {
    border-bottom: 1px solid ${tokens.color.ui_border_light};
  }

  &:last-of-type td {
    border: none;
  }

  &:hover {
    cursor: pointer;
    background-color: ${tokens.color.bg_hover};
    transition: background-color 300ms linear;
  }
`;

const Cell = styled.td`
  padding: ${tokens.spacer.x1pt5} ${tokens.spacer.x2};

  &:last-of-type {
    padding-left: ${tokens.spacer.x1};
  }
`;

const FixedCell = styled(Cell)`
  width: 1%;
  white-space: nowrap;
`;

const TrimingCell = styled(Cell)`
  max-width: 1px;
`;

const TrimmedText = styled(Text)`
  margin-bottom: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TRACKING_UI_POSITION = 'Space Overview Surveys Card';

export function RecentSurveyItem({
  id,
  name,
  enabled,
  responseCount,
  displayType,
}: RecentSurvey): JSX.Element {
  const { t } = useTranslation();
  const { data: currentSite } = useCurrentSite();
  const { click: trackClick } = useTrack();
  const onViewSurveyClick = () => {
    if (currentSite) {
      trackClick('View Survey', TRACKING_UI_POSITION, {});
      navigateTo(SURVEYS.RESPONSES.path, { siteId: currentSite.id, surveyId: id });
    }
  };

  return (
    <Row data-testid="card-survey-item" role="button" onClick={onViewSurveyClick}>
      <TrimingCell>
        <TrimmedText as="p" textColor="text_default" textType="body2">
          {name}
        </TrimmedText>
        <Text style={{ marginBottom: '0px' }} as="p" textColor="text_light" textType="caption">
          {t(`survey.display-type.${displayType}`, { ns: 'feedback' })}
        </Text>
      </TrimingCell>
      <FixedCell style={{ textAlign: 'right' }}>
        <Text as="span" textColor="text_light" textType="caption">
          {enabled
            ? t('activation:dashboard.surveys-card.responses-count', {
                count: responseCount,
              })
            : t('activation:dashboard.surveys-card.inactive')}
        </Text>
      </FixedCell>
      <FixedCell>
        <WidgetGhostButton size="s" onClick={onViewSurveyClick} type="button">
          {t('activation:dashboard.surveys-card.view-responses')}
        </WidgetGhostButton>
      </FixedCell>
    </Row>
  );
}
